import { ErrorBoundary } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { useCallback, useMemo } from 'react';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useServerStorage } from 'utils/useServerStorage';

import { ElevioCard } from '../../integrations/elevio/src/components/ElevioCard';
import { KnowledgeErrorPlaceholder } from '../../integrations/elevio/src/components/KnowledgeErrorPlaceholder';
import { KnowledgeSuggestions } from '../../integrations/knowledge/components/knowledgeSuggestions';
import { CardSection } from '../components/cardSection/cardSection';
import {
  defaultKnowledgeOrder,
  SidebarCardId,
} from '../conversationSidebar.types';
import { useCardVisibility } from '../hooks/useCardVisibility';
import { useSidebarMode } from '../hooks/useSidebarMode';
import { mergeOrder, reorderArray } from '../utils/reorder';

import knowledgeCardStyles from './KnowledgeCard.module.scss';

type KnowledgeCardProps = {
  isMinimized: boolean;
  index: number;
};

const type = Symbol('knowledgeCard');

export function SuggestionCards({
  isMinimized,
  index,
}: KnowledgeCardProps): JSX.Element {
  const { hiddenCards } = useCardVisibility();
  const sidebarMode = useSidebarMode();

  const { elevioSuggestions, knowledgeSuggestions } = useFeatureFlag([
    'elevio-suggestions',
    'knowledge-suggestions',
  ]);

  const hasKnowledgeReadAccess = useHasAccess('knowledge.content:r');

  const isKnowledgeOnForUser = knowledgeSuggestions && hasKnowledgeReadAccess;

  const storage = useServerStorage('user');
  const cardOrder = useMemo(
    () =>
      mergeOrder(
        storage.data.conversation?.sidebarKnowledgeOrder,
        defaultKnowledgeOrder,
      ),
    [storage.data.conversation?.sidebarKnowledgeOrder],
  );

  const handleMove = useCallback(
    ({ dragIndex, hoverIndex }: { dragIndex: number; hoverIndex: number }) => {
      storage.set({
        conversation: (draft) => {
          draft.sidebarKnowledgeOrder = reorderArray(
            storage.data.conversation?.sidebarKnowledgeOrder,
            defaultKnowledgeOrder,
            dragIndex,
            hoverIndex,
          );
        },
      });
    },
    [storage],
  );

  const renderCard = (cardId: SidebarCardId, index: number) => {
    switch (cardId) {
      case 'knowledge-suggestions':
        return isKnowledgeOnForUser &&
          (sidebarMode === 'visibility' ||
            !hiddenCards.has('knowledge-suggestions')) ? (
          <div
            key={cardId}
            data-testid="knowledgeCard"
            className={knowledgeCardStyles.knowledgeCard}
          >
            <KnowledgeSuggestions
              isMinimized={isMinimized}
              index={index}
              type={type}
              onMove={handleMove}
            />
          </div>
        ) : null;
      case 'elevio':
        return elevioSuggestions &&
          (sidebarMode === 'visibility' || !hiddenCards.has('elevio')) ? (
          <div key={cardId} className={knowledgeCardStyles.elevioCard}>
            <ElevioCard
              isMinimized={isMinimized}
              index={index}
              type={type}
              onMove={handleMove}
            />
          </div>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <ErrorBoundary fallback={<KnowledgeErrorPlaceholder />}>
      {elevioSuggestions || isKnowledgeOnForUser ? (
        <CardSection
          title={!isMinimized ? 'Knowledge' : undefined}
          index={index}
          isMinimized={isMinimized}
        >
          {cardOrder.map((id, idx) => renderCard(id, idx))}
        </CardSection>
      ) : (
        <></>
      )}
    </ErrorBoundary>
  );
}
